import { useMutation, useQueryClient, useQuery } from "react-query";
import { useNotify } from "react-admin";
import gql from "graphql-tag";
import { graphqlFetch } from "../../../lib/graphqlClient";

const GET_REWARD_LIST_QUERY = gql`
  query getRewardList($input: RewardListInput!) {
    rewardListByAdmin(input: $input) {
      data {
        rewards {
          id
          amount
          category
          image
          label
          stock
          available
        }
      }
      error
    }
  }
`;

const MANUAL_REWARD_REDEMPTION_MUTATION = gql`
  mutation manualRewardRedemption($input: PurchaseRewardInput!) {
    manualPurchaseReward(input: $input) {
      code
      error
      status
    }
  }
`;

const RESOLVE_REWARD_SUBSCRIBER_QUERY = gql`
  query resolveRewardSubscriber($input: ResolveRewardSubscriberInput!) {
    resolveRewardSubscriber(input: $input) {
      status
      subscriber {
        id
        name
        country
        phone
        is_blocked
        is_subscriber
      }
      error
      trace_id
    }
  }
`;

const EXPORT_REWARD_REDEMPTION_HISTORY_MUTATION = gql`
  mutation ExportRewardRedemptionHistory($input: ReportInput!) {
    report(input: $input) {
      status
      error
    }
  }
`;

const RETRY_BALANCE_TRANSFER_REWARD_MUTATION = gql`
  mutation RetryBalanceTransferReward(
    $input: RetryBalanceTransferRewardInput!
  ) {
    retryBalanceTransferReward(input: $input) {
      status
      error
      trace_id
    }
  }
`;

// Reward list query using GraphQL. Input shape aligns with backend contract.
export interface RewardListFilterInput {
  category: string[]; // required by API
}

export interface RewardListPaginationInput {
  page: number;
  perPage: number;
}

export interface RewardListSortInput {
  field: string;
  order: "ASC" | "DESC";
}

export interface RewardListInput {
  filter?: RewardListFilterInput;
  pagination: RewardListPaginationInput;
  sort: RewardListSortInput;
  subscriber_id?: string;
  platform?: string;
}

export const useRewardListQuery = (
  input: RewardListInput,
  options: any = {}
) => {
  return useQuery(
    ["rewardList", input],
    () =>
      graphqlFetch({
        query: GET_REWARD_LIST_QUERY,
        // IMPORTANT: GraphQL expects a variable named `input`
        variables: { input },
      }),
    {
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: "always",
      refetchOnReconnect: "always",
      refetchOnWindowFocus: true,
      ...options,
    }
  );
};

export const useManualRedemption = () => {
  const queryClient = useQueryClient();
  const notify = useNotify();

  const manualRewardRedeem = useMutation(
    (variables: any) =>
      graphqlFetch({
        query: MANUAL_REWARD_REDEMPTION_MUTATION,
        variables,
      }),
    {
      onSuccess: (data) => {
        // Invalidate and refetch relevant queries
        queryClient.invalidateQueries(["rewards"]);
        queryClient.invalidateQueries(["rewardCatalogues"]);
        queryClient.invalidateQueries(["rewardCodes"]);

        notify("ra.notification.created", { type: "success" });
      },
      onError: (error: Error) => {
        notify(`Error creating reward: ${error.message}`, { type: "error" });
      },
    }
  );

  return {
    manualRewardRedeem: manualRewardRedeem,
    manualRewardRedeemCodeAsync: (rewardCodeData: any) => {
      return manualRewardRedeem.mutateAsync({ input: rewardCodeData });
    },
  };
};

export interface ResolveRewardSubscriberInput {
  phone_number: string;
  platform: string;
}

export interface ResolvedRewardSubscriber {
  id: string;
  name: string;
  country: string;
  phone: string;
  is_blocked: boolean;
  is_subscriber: boolean;
}

export interface ResolveRewardSubscriberResult {
  status: boolean;
  subscriber: ResolvedRewardSubscriber | null;
  error: string;
  trace_id: string;
}

// Read-only subscriber-resolution action; shares the backend's identical
// resolution used by the redemption mutation itself, so a confirmation shown
// here cannot diverge from the subscriber the mutation actually redeems against.
export const resolveRewardSubscriber = (
  input: ResolveRewardSubscriberInput
): Promise<{ resolveRewardSubscriber: ResolveRewardSubscriberResult }> =>
  graphqlFetch({
    query: RESOLVE_REWARD_SUBSCRIBER_QUERY,
    variables: { input },
  });

export interface RewardRedemptionHistoryExportFilter {
  created_at_from?: string;
  created_at_to?: string;
  status?: string | string[];
  platform?: string;
  booking_code?: string;
}

export interface ExportReportResult {
  status: boolean;
  error: string | null;
}

// Triggers the `reward-redemption-history` report via the `report` Hasura
// action (m21-ng-core-app-service `hasura/metadata/actions.yaml`), the same
// action/pipeline `exportSpinWheelWinningHistory` uses for
// `spin-wheel-winning-history` — NOT the legacy `reports` table CRUD
// (`src/provider/reports.ts`) `ExportSmsButton` uses, which this backend's
// `ReportGenerator` job never processes.
export const exportRewardRedemptionHistory = async (
  filter: RewardRedemptionHistoryExportFilter
): Promise<ExportReportResult> => {
  const response = await graphqlFetch<{ report: ExportReportResult }>({
    query: EXPORT_REWARD_REDEMPTION_HISTORY_MUTATION,
    variables: {
      input: {
        type: "reward-redemption-history",
        data: { filter, export_type: "xlsx" },
      },
    },
  });
  return response.report;
};

export const useRetryBalanceTransfer = () => {
  const retryBalanceTransfer = useMutation(
    (rewardLogId: string) =>
      graphqlFetch({
        query: RETRY_BALANCE_TRANSFER_REWARD_MUTATION,
        variables: { input: { reward_log_id: rewardLogId } },
      }),
    {}
  );

  return {
    retryBalanceTransfer,
    retryBalanceTransferAsync: (rewardLogId: string) =>
      retryBalanceTransfer.mutateAsync(rewardLogId),
  };
};
